import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Grid,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { createTenant, inviteManager } from '../services/apiService.ts';
import { MODULE_OPTIONS } from '../constants/modules.ts';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormState {
  name: string;
  subdomain: string;
  managerEmail: string;
  city: string;
  country: string; // ISO-2
  enabledModules: string[];
  features: string[];
}

const initialState: FormState = {
  name: '',
  subdomain: '',
  managerEmail: '',
  city: '',
  country: '',
  enabledModules: ['RESTAURANT'],
  features: [],
};

const CreateTenantDialog: React.FC<Props> = ({ open, onClose, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState<FormState>(initialState);
  const [loading, setLoading] = useState(false);

  const handleChange = (field: keyof FormState) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    let value = e.target.value;
    if (field === 'country') {
      value = value.toUpperCase();
    }
    setForm({ ...form, [field]: value });
  };

  const handleCreate = async () => {
    if (!form.name.trim() || !form.subdomain.trim() || !form.city.trim() || !/^([A-Z]{2})$/.test(form.country.trim())) {
      const parts:string[] = [];
      if (!form.name.trim()) parts.push('name');
      if (!form.subdomain.trim()) parts.push('subdomain');
      if (!form.city.trim()) parts.push('city');
      if (!/^([A-Z]{2})$/.test(form.country.trim())) parts.push('country (ISO-2)');
      enqueueSnackbar(`Please complete: ${parts.join(', ')}`, { variant: 'warning' });
      return;
    }
    try {
      setLoading(true);
      const tenant = await createTenant({
        name: form.name.trim(),
        subdomain: form.subdomain.trim(),
        city: form.city.trim(),
        country: form.country.trim(),
        enabledModules: form.enabledModules,
        features: form.features,
      });
      if (form.managerEmail.trim()) {
        try {
          await inviteManager(tenant.id, form.managerEmail.trim());
        } catch (invErr: any) {
          const friendly = invErr.response?.data?.message || invErr.response?.data?.error || invErr.message;
          enqueueSnackbar(friendly, { variant: 'error' });
        }
      }
      enqueueSnackbar('Tenant created', { variant: 'success' });
      setForm(initialState);
      onSuccess();
      onClose();
    } catch (err: any) {
      const apiMsg = err?.response?.data?.message;
      const errors = err?.response?.data?.errors;
      const details = errors ? Object.entries(errors).map(([k,v])=>`${k}: ${v}`).join('; ') : '';
      const friendly = details ? `${apiMsg || 'Validation failed'}: ${details}` : (apiMsg || err.message);
      enqueueSnackbar(friendly, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setForm(initialState);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Create Tenant</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} mt={1}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Name"
              value={form.name}
              onChange={handleChange('name')}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Subdomain"
              value={form.subdomain}
              onChange={handleChange('subdomain')}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="City"
              value={form.city}
              onChange={handleChange('city')}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Country (ISO-2)"
              value={form.country}
              onChange={handleChange('country')}
              fullWidth
              required
              inputProps={{ maxLength: 2 }}
              helperText="Ex: RO, US"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Manager email (optional)"
              value={form.managerEmail}
              onChange={handleChange('managerEmail')}
              fullWidth
              type="email"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              SelectProps={{ multiple: true }}
              label="Enabled modules"
              value={form.enabledModules}
              onChange={(e) => setForm({ ...form, enabledModules: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value as string[] })}
              fullWidth
              helperText="Pick the product modules sold to this tenant"
            >
              {MODULE_OPTIONS.map((m) => (
                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Add-ons / features (comma separated)"
              value={form.features.join(', ')}
              onChange={(e) => setForm({ ...form, features: e.target.value.split(',').map((v) => v.trim()).filter(Boolean) })}
              fullWidth
              helperText="Examples: inventory, workforce, reports, housekeeping"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button onClick={handleCreate} disabled={loading || !form.name || !form.subdomain}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTenantDialog;
